import { useCallback, useMemo } from "react";
import { Box, Button, Paper, TextField, Typography } from "@mui/material";
import DeleteOutlineIconButton from "components/common/DeleteOutlineIconButton";
import Breadcrumbs from "components/common/Breadcrumbs";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import useEmployees from "hooks/employees/useEmployees";
import { api } from "services/api.service";
import { surfacePanelSx, portalDataGridSx } from "constants/pageSurfaces";
import { Employee, EmployeeAssignedAsset } from "types/employee";
import EmployeeAssignedAssetSummary from "pages/Employees/EmployeeAssignedAssetSummary";
import { formatEmployeeHomeOneLine } from "pages/WorkOrders/workOrderDisplay";

const searchFieldSx = {
  minWidth: { xs: "100%", sm: 260 },
  flex: { xs: "1 1 100%", sm: "0 0 auto" },
  "& .MuiOutlinedInput-root": { bgcolor: "#ffffff" },
};

export default function EmployeesList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get("search") ?? "";
  const { employees, isLoading, refetch } = useEmployees({ enableListSearch: true });

  const handleSearchChange = useCallback(
    (q: string) => {
      const next = new URLSearchParams(searchParams);
      if (q) next.set("search", q);
      else next.delete("search");
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const handleDelete = useCallback(
    (emp: Employee) => {
      if (!emp.uuid || !window.confirm("Remove this employee?")) return;
      api.delete(`/employees/${emp.uuid}`).then(() => refetch());
    },
    [refetch]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "firstName",
        headerName: "Name",
        minWidth: 170,
        flex: 0.9,
        sortable: false,
        renderCell: (params) => {
          const e = params.row;
          const label = [e.firstName, e.lastName].filter(Boolean).join(" ") || "—";
          return (
            <RouterLink to={`/employees/${e.uuid}/edit`} style={{ color: "inherit" }}>
              {label}
            </RouterLink>
          );
        },
      },
      {
        field: "externalEmployeeId",
        headerName: "External ID",
        minWidth: 110,
        flex: 0.5,
        valueGetter: (params) => params.row.externalEmployeeId || "—",
      },
      {
        field: "email",
        headerName: "Email",
        minWidth: 200,
        flex: 1,
        valueGetter: (params) => params.row.email || "—",
      },
      {
        field: "office",
        headerName: "Office",
        minWidth: 160,
        flex: 0.8,
        sortable: false,
        valueGetter: (params) => {
          const o = params.row.officeLocation;
          return o?.name || o?.address || o?.city || "—";
        },
      },
      {
        field: "remoteWorker",
        headerName: "Remote",
        width: 90,
        sortable: false,
        valueGetter: (params) => (params.row.remoteWorker ? "Yes" : "—"),
      },
      {
        field: "assets",
        headerName: "Assets",
        minWidth: 400,
        width: 440,
        maxWidth: 520,
        sortable: false,
        renderCell: (params) => {
          const emp = params.row;
          if (!emp.assets?.length) return "—";
          return (
            <Box sx={{ py: 0.25, maxWidth: 500 }}>
              {emp.assets.slice(0, 2).map((a: EmployeeAssignedAsset) => (
                <EmployeeAssignedAssetSummary key={a.uuid} asset={a} dense />
              ))}
              {emp.assets.length > 2 ? (
                <Typography variant="caption" color="text.secondary">
                  +{emp.assets.length - 2} more
                </Typography>
              ) : null}
            </Box>
          );
        },
      },
      {
        field: "home",
        headerName: "Home address",
        minWidth: 200,
        flex: 0.9,
        sortable: false,
        valueGetter: (params) => formatEmployeeHomeOneLine(params.row),
      },
      {
        field: "jobTitle",
        headerName: "Job title",
        minWidth: 120,
        flex: 0.6,
        valueGetter: (params) => params.row.jobTitle || "—",
      },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        valueGetter: (params) => params.row.status || "—",
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        width: 108,
        align: "right",
        headerAlign: "right",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <DeleteOutlineIconButton
            aria-label="Remove employee"
            onClick={() => handleDelete(params.row)}
          />
        ),
      },
    ],
    [handleDelete]
  );

  return (
    <div>
      <Box>
        <Breadcrumbs
          data={[
            { label: "Dashboard", to: "/dashboard" },
            { label: "Employees" },
          ]}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
          mt: 1,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Employees
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "stretch", sm: "flex-end" },
          }}
        >
          <TextField
            size="small"
            placeholder="Search employees…"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={searchFieldSx}
          />
          <Button component={RouterLink} variant="contained" size="large" to="/employees/new" sx={{ flexShrink: 0 }}>
            New employee
          </Button>
        </Box>
      </Box>
      <Paper variant="outlined" sx={{ ...surfacePanelSx, width: "100%", overflow: "hidden" }}>
        <DataGridPro
          autoHeight
          disableColumnMenu
          loading={isLoading}
          rows={employees}
          columns={columns}
          getRowId={(row) => row.id}
          rowHeight={92}
          sx={portalDataGridSx}
          initialState={{ pinnedColumns: { right: ["actions"] } }}
          hideFooter
          components={{
            NoRowsOverlay: () => (
              <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>No employees yet.</Box>
            ),
          }}
        />
      </Paper>
    </div>
  );
}
